import { Controller } from "@hotwired/stimulus";

const STORAGE_CURRENT_STEP_KEY = "__stepper_current_step";
const STORAGE_VISITED_STEPS_KEY = "__stepper_visited_steps";

export default class extends Controller {
  static targets = ["step", "indicator", "line", "prevButton", "nextButton", "submitButton"];

  static values = {
    // Core behavior
    currentStep: { type: Number, default: 0 }, // Current active step index (0-based)
    validateOnNext: { type: Boolean, default: true }, // Whether to validate required fields before advancing
    allowSkip: { type: Boolean, default: false }, // Allow jumping to any step (bypasses visited-only restriction)
    scrollOnChange: { type: Boolean, default: false }, // Scroll to stepper top when changing steps
    saveProgress: { type: Boolean, default: true }, // Save form data to sessionStorage for persistence
    storageKey: { type: String, default: "stepper-progress" }, // sessionStorage key for saving progress
    cloak: { type: Boolean, default: true }, // Hide UI until controller is connected
    animateEntrance: { type: Boolean, default: true }, // Animate first reveal

    // Styling: Default classes that can be overridden via data attributes
    indicatorBaseClass: { type: String, default: "" },
    indicatorCompleteClass: {
      type: String,
      default:
        "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 cursor-pointer hover:ring-2 hover:ring-black/5 dark:hover:ring-white/10",
    },
    indicatorCurrentClass: {
      type: String,
      default:
        "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 ring-2 ring-black/5 dark:ring-white/10 cursor-pointer",
    },
    indicatorVisitedClass: {
      type: String,
      default:
        "bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 cursor-pointer hover:bg-neutral-200 dark:hover:bg-neutral-700 hover:ring-2 hover:ring-black/5 dark:hover:ring-white/10",
    },
    indicatorUpcomingClass: {
      type: String,
      default:
        "bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500 border border-neutral-200 dark:border-neutral-700 cursor-not-allowed opacity-75",
    },
    lineBaseClass: { type: String, default: "" },
    lineCompleteClass: {
      type: String,
      default: "bg-neutral-900 dark:bg-white",
    },
    lineIncompleteClass: {
      type: String,
      default: "bg-neutral-200 dark:bg-neutral-700",
    },
  };

  connect() {
    this.formData = this.loadProgress() || {};
    this.currentStepValue = this.loadCurrentStep();
    this.visitedSteps = this.loadVisitedSteps();
    this.hasAnimatedInitialProgress = false;

    this.handlePersistProgress = this.persistProgressSnapshot.bind(this);
    this.handleKeydown = this.handleKeydownEvent.bind(this);

    this.element.addEventListener("input", this.handlePersistProgress);
    this.element.addEventListener("change", this.handlePersistProgress);
    this.element.addEventListener("keydown", this.handleKeydown);
    window.addEventListener("beforeunload", this.handlePersistProgress);
    window.addEventListener("pagehide", this.handlePersistProgress);

    this.restoreAllStepData();
    this.updateView();
    this.revealCloakedStepper();
  }

  disconnect() {
    if (!this.handlePersistProgress) return;

    this.persistProgressSnapshot();
    this.element.removeEventListener("input", this.handlePersistProgress);
    this.element.removeEventListener("change", this.handlePersistProgress);
    this.element.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener("beforeunload", this.handlePersistProgress);
    window.removeEventListener("pagehide", this.handlePersistProgress);
  }

  handleKeydownEvent(event) {
    if (event.key !== "Enter") return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    // Don't intercept Enter on textareas (allow new lines)
    if (target.tagName === "TEXTAREA") return;

    // Don't intercept Enter on buttons (let them handle their own action)
    if (target.tagName === "BUTTON") return;

    // For all other form elements (input, select), trigger next step or submit
    if (target.matches("input, select")) {
      event.preventDefault();

      if (this.currentStepValue === this.stepTargets.length - 1) {
        this.submit(event);
      } else {
        this.next(event);
      }
    }
  }

  revealCloakedStepper() {
    if (this.element.dataset.stepperRevealed === "true") {
      return;
    }

    const shouldAnimateEntrance = this.animateEntranceValue;
    if (!this.cloakValue && !shouldAnimateEntrance) {
      return;
    }

    this.element.dataset.stepperRevealed = "true";

    requestAnimationFrame(() => {
      if (this.cloakValue) {
        this.element.style.visibility = "visible";
      }

      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (!shouldAnimateEntrance || prefersReducedMotion) {
        this.element.style.opacity = "1";
        this.element.style.transform = "none";
        return;
      }

      // Keep this lightweight: one wrapper animation avoids child-level jank.
      this.element.style.willChange = "opacity, transform";

      const animation = this.element.animate(
        [
          { opacity: 0, transform: "translateY(8px)" },
          { opacity: 1, transform: "translateY(0)" },
        ],
        {
          duration: 360,
          easing: "cubic-bezier(0.22, 1, 0.36, 1)",
          fill: "forwards",
        },
      );

      const finalizeStyles = () => {
        this.element.style.opacity = "1";
        this.element.style.transform = "none";
        this.element.style.willChange = "auto";
      };

      animation.onfinish = finalizeStyles;
      animation.oncancel = finalizeStyles;
    });
  }

  next(event) {
    event?.preventDefault();

    // Validate current step if validation is enabled
    if (this.validateOnNextValue && !this.validateCurrentStep()) {
      return;
    }

    // Save current step data
    if (this.saveProgressValue) {
      this.saveCurrentStepData();
    }

    // Move to next step if not at the end
    if (this.currentStepValue < this.stepTargets.length - 1) {
      this.currentStepValue++;
      this.visitedSteps.add(this.currentStepValue);
      this.updateView();
      if (this.scrollOnChangeValue) this.scrollToTop();
    }
  }

  previous(event) {
    event?.preventDefault();

    if (this.saveProgressValue) {
      this.saveCurrentStepData();
    }

    if (this.currentStepValue > 0) {
      this.currentStepValue--;
      this.updateView();
      if (this.scrollOnChangeValue) this.scrollToTop();
    }
  }

  goToStep(event) {
    event?.preventDefault();
    const targetStep = Number.parseInt(event.currentTarget.dataset.step, 10);

    if (!Number.isInteger(targetStep)) return;

    // Allow navigation to:
    // 1. Any visited step (includes previous steps and future steps that were already visited)
    // 2. Any step if allowSkip is enabled
    if (this.allowSkipValue || this.visitedSteps.has(targetStep)) {
      if (this.saveProgressValue) {
        this.saveCurrentStepData();
      }

      this.currentStepValue = targetStep;
      this.updateView();
      if (this.scrollOnChangeValue) this.scrollToTop();
    }
  }

  validateCurrentStep() {
    const currentStep = this.stepTargets[this.currentStepValue];
    if (!currentStep) return true;

    const requiredInputs = currentStep.querySelectorAll("input[required], select[required], textarea[required]");
    let isValid = true;

    requiredInputs.forEach((input) => {
      if (!input.checkValidity()) {
        isValid = false;
        input.reportValidity();
      }
    });

    const validateEvent = new CustomEvent("stepper:validate", {
      detail: { step: this.currentStepValue, isValid },
      bubbles: true,
      cancelable: true,
    });

    this.element.dispatchEvent(validateEvent);

    if (validateEvent.defaultPrevented) {
      isValid = false;
    }

    return isValid;
  }

  saveCurrentStepData() {
    const currentStep = this.stepTargets[this.currentStepValue];
    if (!currentStep) return;

    this.persistStepData(currentStep);
    this.persistProgress();

    this.element.dispatchEvent(
      new CustomEvent("stepper:save", {
        detail: { step: this.currentStepValue, data: this.formData },
        bubbles: true,
      }),
    );
  }

  persistStepData(step) {
    const inputs = step.querySelectorAll("input, select, textarea");

    inputs.forEach((input) => {
      if (!input.name) return;

      if (input.type === "checkbox") {
        this.formData[input.name] = input.checked;
      } else if (input.type === "radio") {
        if (input.checked) {
          this.formData[input.name] = input.value;
        }
      } else {
        this.formData[input.name] = input.value;
      }
    });
  }

  persistProgress() {
    if (!this.saveProgressValue || typeof sessionStorage === "undefined") return;

    const payload = {
      ...this.formData,
      [STORAGE_CURRENT_STEP_KEY]: this.currentStepValue,
      [STORAGE_VISITED_STEPS_KEY]: Array.from(this.visitedSteps),
    };

    sessionStorage.setItem(this.storageKeyValue, JSON.stringify(payload));
  }

  persistProgressSnapshot() {
    if (!this.saveProgressValue) return;

    const currentStep = this.stepTargets[this.currentStepValue];
    if (currentStep) this.persistStepData(currentStep);
    this.persistProgress();
  }

  loadProgress() {
    if (!this.saveProgressValue || typeof sessionStorage === "undefined") return {};

    const saved = sessionStorage.getItem(this.storageKeyValue);
    if (!saved) return {};

    try {
      const parsed = JSON.parse(saved);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  loadCurrentStep() {
    const savedStep = Number.parseInt(this.formData[STORAGE_CURRENT_STEP_KEY], 10);
    if (Number.isNaN(savedStep)) return this.currentStepValue;

    const maxStep = Math.max(this.stepTargets.length - 1, 0);
    return Math.min(Math.max(savedStep, 0), maxStep);
  }

  loadVisitedSteps() {
    const maxStep = Math.max(this.stepTargets.length - 1, 0);
    const savedVisitedSteps = this.formData[STORAGE_VISITED_STEPS_KEY];

    const visited = new Set();

    if (Array.isArray(savedVisitedSteps)) {
      savedVisitedSteps.forEach((step) => {
        if (Number.isInteger(step) && step >= 0 && step <= maxStep) {
          visited.add(step);
        }
      });
    }

    // Ensure previously completed path can always be navigated.
    for (let i = 0; i <= this.currentStepValue; i++) {
      visited.add(i);
    }

    visited.add(0);
    return visited;
  }

  clearProgress() {
    this.formData = {};
    this.visitedSteps = new Set([0]);

    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(this.storageKeyValue);
    }
  }

  updateView() {
    this.stepTargets.forEach((step, index) => {
      if (index === this.currentStepValue) {
        step.classList.remove("hidden");
        step.classList.add("block");
        this.restoreStepData(step);
      } else {
        step.classList.add("hidden");
        step.classList.remove("block");
      }
    });

    this.updateStepScopedElements();
    this.updateIndicators();
    this.updateButtons();
    this.updateReviewSections();

    this.element.dispatchEvent(
      new CustomEvent("stepper:change", {
        detail: {
          step: this.currentStepValue,
          totalSteps: this.stepTargets.length,
          isFirst: this.currentStepValue === 0,
          isLast: this.currentStepValue === this.stepTargets.length - 1,
        },
        bubbles: true,
      }),
    );
  }

  updateStepScopedElements() {
    const stepScopedElements = this.element.querySelectorAll("[data-stepper-only-step]");

    stepScopedElements.forEach((element) => {
      const onlyStep = Number.parseInt(element.dataset.stepperOnlyStep, 10);
      const showOnCurrentStep = !Number.isNaN(onlyStep) && onlyStep === this.currentStepValue;
      element.classList.toggle("hidden", !showOnCurrentStep);
    });
  }

  restoreStepData(step) {
    const inputs = step.querySelectorAll("input, select, textarea");

    inputs.forEach((input) => {
      if (input.name && this.formData[input.name] !== undefined) {
        if (input.type === "checkbox") {
          input.checked = this.formData[input.name];
        } else if (input.type === "radio") {
          input.checked = input.value === this.formData[input.name];
        } else {
          input.value = this.formData[input.name];
        }
      }
    });
  }

  restoreAllStepData() {
    this.stepTargets.forEach((step) => this.restoreStepData(step));
  }

  updateIndicators() {
    this.indicatorTargets.forEach((indicator, index) => {
      const state = this.getIndicatorState(index);

      this.applyIndicatorState(indicator, state);
      indicator.setAttribute("aria-current", state === "current" ? "step" : "false");
    });

    if (this.hasLineTarget) {
      this.lineTargets.forEach((line, index) => {
        this.applyLineState(line, index < this.currentStepValue);
      });
    }
  }

  getIndicatorState(index) {
    if (index < this.currentStepValue) return "complete";
    if (index === this.currentStepValue) return "current";
    if (this.visitedSteps.has(index)) return "visited";
    return "upcoming";
  }

  applyIndicatorState(indicator, state) {
    const numberSpan = indicator.querySelector(".step-number");
    const checkIcon = indicator.querySelector(".step-check");

    if (state === "complete") {
      numberSpan?.classList.add("hidden");
      checkIcon?.classList.remove("hidden");
    } else {
      numberSpan?.classList.remove("hidden");
      checkIcon?.classList.add("hidden");
    }

    const allStateClasses = [
      this.indicatorCompleteClassValue,
      this.indicatorCurrentClassValue,
      this.indicatorVisitedClassValue,
      this.indicatorUpcomingClassValue,
    ]
      .join(" ")
      .split(" ")
      .filter((cls) => cls);

    allStateClasses.forEach((cls) => indicator.classList.remove(cls));

    if (this.indicatorBaseClassValue) {
      this.indicatorBaseClassValue.split(" ").forEach((cls) => {
        if (cls) indicator.classList.add(cls);
      });
    }

    const stateClassMap = {
      complete: this.indicatorCompleteClassValue,
      current: this.indicatorCurrentClassValue,
      visited: this.indicatorVisitedClassValue,
      upcoming: this.indicatorUpcomingClassValue,
    };

    const stateClasses = stateClassMap[state];
    if (stateClasses) {
      stateClasses.split(" ").forEach((cls) => {
        if (cls) indicator.classList.add(cls);
      });
    }
  }

  applyLineState(line, isComplete) {
    const allStateClasses = [this.lineCompleteClassValue, this.lineIncompleteClassValue]
      .join(" ")
      .split(" ")
      .filter((cls) => cls);

    allStateClasses.forEach((cls) => line.classList.remove(cls));

    if (this.lineBaseClassValue) {
      this.lineBaseClassValue.split(" ").forEach((cls) => {
        if (cls) line.classList.add(cls);
      });
    }

    const stateClass = isComplete ? this.lineCompleteClassValue : this.lineIncompleteClassValue;
    if (stateClass) {
      stateClass.split(" ").forEach((cls) => {
        if (cls) line.classList.add(cls);
      });
    }
  }

  updateButtons() {
    const isFirstStep = this.currentStepValue === 0;
    const isLastStep = this.currentStepValue === this.stepTargets.length - 1;

    if (this.hasPrevButtonTarget) {
      this.prevButtonTargets.forEach((btn) => {
        if (isFirstStep) {
          btn.classList.add("hidden");
          btn.disabled = true;
        } else {
          btn.classList.remove("hidden");
          btn.disabled = false;
        }
      });
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTargets.forEach((btn) => {
        if (isLastStep) {
          btn.classList.add("hidden");
        } else {
          btn.classList.remove("hidden");
        }
      });
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTargets.forEach((btn) => {
        if (isLastStep) {
          btn.classList.remove("hidden");
        } else {
          btn.classList.add("hidden");
          this.setSubmitButtonLoadingState(btn, false);
        }
      });
    }
  }

  scrollToTop() {
    this.element.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  updateReviewSections() {
    this.element.querySelectorAll("[data-review-field]").forEach((field) => {
      const fieldName = field.dataset.reviewField;

      if (fieldName === "full_name" && this.formData.first_name && this.formData.last_name) {
        field.textContent = `${this.formData.first_name} ${this.formData.last_name}`;
        return;
      }

      const value = this.formData[fieldName];
      field.textContent = value !== undefined && value !== null && value !== "" ? value : "—";
    });

    this.updateProgressBar();
    this.updateStepCounter();
    this.updateStepLabel();
  }

  updateProgressBar() {
    const progressBar = this.element.querySelector("[data-stepper-progress-bar]");
    if (!progressBar || this.stepTargets.length === 0) return;

    const percentage = ((this.currentStepValue + 1) / this.stepTargets.length) * 100;

    if (!this.hasAnimatedInitialProgress) {
      this.hasAnimatedInitialProgress = true;
      progressBar.style.width = "0%";

      requestAnimationFrame(() => {
        progressBar.style.width = `${percentage}%`;
      });

      return;
    }

    progressBar.style.width = `${percentage}%`;
  }

  updateStepCounter() {
    const stepCounter = this.element.querySelector("[data-stepper-step-counter]");
    if (stepCounter) {
      stepCounter.textContent = `Step ${this.currentStepValue + 1} of ${this.stepTargets.length}`;
    }
  }

  updateStepLabel() {
    const stepLabel = this.element.querySelector("[data-stepper-step-label]");
    if (stepLabel?.dataset.stepLabels) {
      const labels = stepLabel.dataset.stepLabels.split(",");
      stepLabel.textContent = labels[this.currentStepValue] || `Step ${this.currentStepValue + 1}`;
    }
  }

  reset() {
    this.currentStepValue = 0;
    this.clearProgress();
    this.updateView();
  }

  submit(event) {
    event?.preventDefault();

    if (this.validateOnNextValue && !this.validateCurrentStep()) {
      return;
    }

    if (this.saveProgressValue) {
      this.saveCurrentStepData();
    }

    const submitEvent = new CustomEvent("stepper:submit", {
      detail: { data: this.formData },
      bubbles: true,
      cancelable: true,
    });

    this.element.dispatchEvent(submitEvent);

    if (!submitEvent.defaultPrevented) {
      const form = this.element.closest("form");
      if (form) {
        this.submitButtonTargets.forEach((button) => {
          button.disabled = true;
          this.setSubmitButtonLoadingState(button, true);
        });

        this.clearProgress();
        form.requestSubmit();
      } else {
        alert("Form submitted! (This is just a preview)");
      }
    }
  }

  setSubmitButtonLoadingState(button, isLoading) {
    const idleContent = button.querySelector("[data-stepper-submit-idle]");
    const loadingContent = button.querySelector("[data-stepper-submit-loading]");
    if (!idleContent || !loadingContent) return;

    idleContent.classList.toggle("hidden", isLoading);
    loadingContent.classList.toggle("hidden", !isLoading);
  }
}
